import { useGetAttachmentsByDocId } from "@/api/attachment";
import {
  getRelativePosition,
  useGetAudiovisual,
  useUpdateAudiovisual,
} from "@/api/audiovisual/audiovisual";
import {
  appendNoDuplicateRecordNumberRefine,
  audiovisualSchema,
} from "@/api/audiovisual/audiovisual-validation";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import * as AF from "@/components/form-items/AbstractForm";
import LinksManagement from "@/components/LinksManagement";
import AddFileModal from "@/components/modal/AddFileModal";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { getMainLanguage } from "@/i18n";
import { FormLayout } from "@/layout/FormLayout";
import { computeFieldAudiovisualSummary } from "@/lib/computeFields";
import { Audiovisual } from "@/lib/types";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

const schema = appendNoDuplicateRecordNumberRefine(
  audiovisualSchema.passthrough(),
);

const EditAudiovisual = ({ audiovisual }: { audiovisual: Audiovisual }) => {
  const form = useForm({
    resolver: standardSchemaResolver(schema),
    defaultValues: audiovisual,
  });

  const { data: attachments } = useGetAttachmentsByDocId(audiovisual.id);

  const audiovisualUpdater = useUpdateAudiovisual();

  const { t } = useTranslation();

  const [, setSearchParam] = useSearchParams();

  const handleResume = (data: any) => {
    return computeFieldAudiovisualSummary(data, {
      language: getMainLanguage(),
    });
  };

  const { relativePosition } = useDashboardParams<Audiovisual>({
    statusField: "audiovisuelPublicationStatus",
    resourceName: "bibliography",
    relativePosition: {
      currentId: audiovisual.id,
      positionFetcher: getRelativePosition,
    },
  });

  const queryClient = useQueryClient();

  return (
    <FormLayout
      onSave={(status) =>
        form.handleSubmit(
          (values) => {
            audiovisualUpdater.mutate(
              {
                ...values,
                ...(status && { audiovisuelPublicationStatus: status }),
              },
              {
                onSuccess: () => {
                  toast({
                    title: t(
                      status === undefined
                        ? "autoSave"
                        : "recordSavedToastTitle",
                    ),
                    variant: "success",
                    description: t("audiovisualSavedToastDescription"),
                  });
                  form.reset({}, { keepValues: true });
                },
              },
            );
          },
          (invalid) => {
            if (invalid.audiovisuelDescriptionNumeroEnregistrement != null) {
              setSearchParam({ tab: "description" });
            }
          },
        )()
      }
      title={audiovisual.audiovisuelDescriptionTitre}
      invalidateOnChange={() => {
        queryClient.invalidateQueries({ queryKey: ["audiovisuals"] });
        queryClient.invalidateQueries({
          queryKey: ["audiovisual", audiovisual.id],
        });
        queryClient.invalidateQueries({
          queryKey: ["attachment", { docId: audiovisual.id }],
        });
      }}
      reference={audiovisual.audiovisuelDescriptionNumeroEnregistrement}
      relativePosition={relativePosition}
      id={audiovisual.id}
      type="audiovisual"
      publicationStatus={audiovisual.audiovisuelPublicationStatus}
      picturesData={
        attachments && { pictures: attachments, type: "audiovisual" }
      }
      form={form}
    >
      <Separator />
      <AF.AbstractForm
        form={form}
        tabs={[
          {
            value: "description",
            label: t("description"),
          },
          { value: "sources", label: t("sources") },
          { value: "files", label: t("fichiers") },
          { value: "links", label: t("linkedEntries") },
        ]}
      >
        <AF.Tab value="description">
          <AF.Col>
            <AF.Text name="audiovisuelDescriptionNumeroEnregistrement" />
            <AF.Text name="audiovisuelDescriptionAuteur" />
            <AF.Checkbox name="audiovisuelDescriptionAuteurAnonyme" />
            <AF.Text name="audiovisuelDescriptionTitre" />
            <AF.RadioGroup
              resettable={true}
              items={[
                { value: "audiovisuelDescriptionTitreSansTitre" },
                { value: "audiovisuelDescriptionTitreInconnu" },
              ]}
            />
            <AF.Text name="audiovisuelDescriptionEmission" />
            <AF.Text name="audiovisuelDescriptionSousTitre" />
            <AF.Row>
              <AF.Text name="audiovisuelDescriptionDateDeCreation" />
              <AF.Checkbox
                name="audiovisuelDescriptionDateDeCreationInconnue"
                align={true}
              />
            </AF.Row>
            <AF.Row>
              <AF.Text name="audiovisuelDescriptionDateDe1EreDiffusion" />
              <AF.Checkbox
                name="audiovisuelDescriptionDateDe1EreDiffusionInconnue"
                align={true}
              />
            </AF.Row>
            <ModifiableList
              name="audiovisuelDescriptionType"
              listKey="liste-types-documents-audiovisuels"
            />
            <AF.Text name="audiovisuelDescriptionRealisation" />
            <AF.Text name="audiovisuelDescriptionProduction" />
            <AF.Text name="audiovisuelDescriptionDiffusion" />
            <AF.Text name="audiovisuelDescriptionDistribution" />
            <ModifiableList
              name="audiovisuelDescriptionPays"
              listKey="liste-pays"
            />
          </AF.Col>
          <AF.Col>
            <AF.RichText
              name="audiovisuelDescriptionResume"
              computeAction={handleResume}
            />
            <AF.Text
              className="w-20"
              name="audiovisuelDescriptionTechniqueDuree"
            />
            <AF.Row>
              <ModifiableList
                name="audiovisuelDescriptionTechniqueFormatCineVideo"
                listKey="liste-formats-cine-video"
              />
              <AF.Checkbox
                name="audiovisuelDescriptionTechniqueCouleur"
                align={true}
              />
            </AF.Row>
            <ModifiableList
              className="w-32"
              name="audiovisuelDescriptionTechniqueFormatSon"
              listKey="liste-formats-son"
            />
            <ModifiableList
              name="audiovisuelDescriptionTechniqueVersionLangue"
              listKey="liste-langues"
            />
            <ModifiableList
              name="audiovisuelDescriptionTechniqueSousTitre"
              listKey="liste-langues"
            />
            <AF.Text name="audiovisuelDescriptionTechniqueAssistantRalisation" />
            <AF.Text name="audiovisuelDescriptionTechniqueImage" />
            <AF.Text name="audiovisuelDescriptionTechniqueMontage" />
            <AF.Text name="audiovisuelDescriptionTechniqueMusique" />
            <AF.Text name="audiovisuelDescriptionTechniqueSon" />
            <AF.Text name="audiovisuelDescriptionTechniqueAutresPostesTechniques" />
            <AF.RichText name="audiovisuelDescriptionNotesDescriptionFilm" />
            <AF.RichText name="audiovisuelDescriptionNotesDeTravail" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="sources">
          <AF.RichText name="sources" />
        </AF.Tab>
        <AF.Tab
          isForm={false}
          value="files"
          headerExtras={<AddFileModal id={audiovisual.id} />}
        >
          <AF.OthersFilesTable docId={audiovisual.id} name="diversfiles" />
        </AF.Tab>
        <AF.Tab value="links">
          <LinksManagement
            id={audiovisual.id}
            type="audiovisual"
            data={audiovisual}
            configuration={{
              fields: [
                {
                  name: "links",
                  type: "links",
                  label: "Liens",
                  links: [
                    {
                      name: "relatedArtwork",
                      label: t("entryartworks"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedExhibition",
                      label: t("entryexhibitions"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedBibliography",
                      label: t("entrybibliographies"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedAuction",
                      label: t("entryauctions"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedDocument",
                      label: t("entrydocuments"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedPhotography",
                      label: t("entryphotographies"),
                      type: "link-simple",
                    },
                  ],
                },
              ],
            }}
          />
        </AF.Tab>
      </AF.AbstractForm>
    </FormLayout>
  );
};

export const EditAudiovisualPage = () => {
  const { id } = useParams();
  const { data, isLoading } = useGetAudiovisual(id);
  return (
    <>
      {data && <EditAudiovisual key={data.id} audiovisual={data} />}
      {!data && !isLoading && (
        <h2 className="p-4">bibliographieNexistePasLabel</h2>
      )}
    </>
  );
};
